import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import PublishIcon from '@mui/icons-material/Publish';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

import type { NurturingPlanController } from '../hooks/useNurturingPlan';
import { PlanVersionSelect } from './PlanVersionSelect';
import { DealerLaunchMaterializationStatus } from 'app/components/ReusableUIComponents/LowLevelComponents/DealerLaunchMaterializationStatus';
import { useDealerLaunchMaterializationStatusClick } from 'app/pages/Header/hooks/useDealerLaunchMaterializationStatusClick';

interface PlanHeaderControlsProps {
  ctrl: NurturingPlanController;
}

/** Keeps header action buttons aligned with the version dropdown (30.75px). */
const actionButtonSx = {
  minHeight: 30.75,
  height: 30.75,
  px: 1.5,
  fontSize: '0.8125rem',
  lineHeight: 1,
  '& .MuiButton-startIcon': { mr: 0.5, ml: 0 },
  '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 16 },
};

/**
 * Version dropdown + Cancel / Save / Publish actions, rendered in the page
 * header (right side, just below the quick-stat chips). Owns its own confirm
 * dialogs.
 */
export const PlanHeaderControls = ({ ctrl }: PlanHeaderControlsProps) => {
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [launchConfirmOpen, setLaunchConfirmOpen] = useState(false);
  const { onStatusClick } = useDealerLaunchMaterializationStatusClick(ctrl.selectedDealer);

  const saveDisabledReason = !ctrl.isDirty
    ? 'No unsaved changes'
    : !ctrl.plan || !ctrl.canSave
    ? 'Generate the plan before saving'
    : 'Save nurturing plan';

  const publishDisabledReason = ctrl.hasPublishedVersion
    ? 'A published version already exists for this dealer'
    : ctrl.isAlreadyPublished
    ? 'This version is already published'
    : ctrl.isDirty
    ? 'Save your changes before publishing'
    : 'Publish nurturing plan';

  const launchDisabledReason = ctrl.showLaunchMaterializationStatus
    ? 'Dealership launch is already in progress or pending'
    : ctrl.hasLaunchDate
    ? 'Dealership is already launched'
    : !ctrl.hasPublishedVersion
      ? 'Publish a nurturing plan before launching'
      : 'Launch dealership';

  const showLaunchAction =
    !ctrl.hasLaunchDate && !ctrl.showLaunchMaterializationStatus;

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 48,
          right: 48,
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        <PlanVersionSelect
          versions={ctrl.versions}
          selectedVersionId={ctrl.selectedVersionId}
          versionsLoading={ctrl.versionsLoading}
          onVersionChange={ctrl.onVersionChange}
        />
        <Button
          variant="outlined"
          size="small"
          sx={actionButtonSx}
          onClick={ctrl.handleCancel}
          disabled={!ctrl.isDirty || ctrl.loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={actionButtonSx}
          onClick={() => setSaveConfirmOpen(true)}
          disabled={!ctrl.canSave || ctrl.saving}
          title={saveDisabledReason}
          startIcon={
            ctrl.saving ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {ctrl.saving ? 'Saving...' : 'Save'}
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          sx={actionButtonSx}
          startIcon={
            ctrl.publishing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PublishIcon />
            )
          }
          onClick={() => setPublishConfirmOpen(true)}
          disabled={
            ctrl.isDirty ||
            ctrl.loading ||
            ctrl.publishing ||
            ctrl.hasPublishedVersion ||
            ctrl.isAlreadyPublished ||
            !ctrl.selectedVersionId
          }
          title={publishDisabledReason}
        >
          {ctrl.publishing ? 'Publishing...' : 'Publish'}
        </Button>

        {ctrl.showLaunchMaterializationStatus ? (
          <DealerLaunchMaterializationStatus
            dealer={ctrl.selectedDealer}
            onClick={onStatusClick}
          />
        ) : showLaunchAction ? (
          <Button
            variant="contained"
            size="small"
            color="primary"
            sx={actionButtonSx}
            startIcon={
              ctrl.launching ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <RocketLaunchIcon />
              )
            }
            onClick={() => setLaunchConfirmOpen(true)}
            disabled={ctrl.launching || !ctrl.hasPublishedVersion}
            title={launchDisabledReason}
          >
            {ctrl.launching ? 'Launching...' : 'Launch'}
          </Button>
        ) : null}
      </Box>

      <ConfirmationDialogMAIQ
        open={saveConfirmOpen}
        onClose={() => setSaveConfirmOpen(false)}
        onConfirm={async () => {
          await ctrl.handleSave();
          setSaveConfirmOpen(false);
        }}
        title="Save Nurturing Plan?"
        message="Save your current nurturing plan configuration for this dealer? This updates the selected version."
        confirmLabel="Confirm Save"
        loading={ctrl.saving}
        loadingLabel="Saving..."
      />

      <ConfirmationDialogMAIQ
        open={publishConfirmOpen}
        onClose={() => setPublishConfirmOpen(false)}
        onConfirm={async () => {
          await ctrl.handlePublish();
          setPublishConfirmOpen(false);
        }}
        title="Publish Nurturing Plan?"
        message={
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Are you sure you want to publish this version? It will become the
              active nurturing plan configuration for this dealer.
            </Typography>
            <Alert severity="warning" variant="outlined">
              Once you publish, no other version can be published for this dealer.
              Make sure all settings and the generated plan are correct before
              continuing.
            </Alert>
          </>
        }
        confirmLabel="Publish"
        loading={ctrl.publishing}
        loadingLabel="Publishing..."
      />

      <ConfirmationDialogMAIQ
        open={launchConfirmOpen}
        onClose={() => setLaunchConfirmOpen(false)}
        onConfirm={async () => {
          await ctrl.handleLaunch();
          setLaunchConfirmOpen(false);
        }}
        title="Launch Dealership?"
        message={
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: ctrl.hasLaunchDate ? 2 : 0 }}>
              This will start Continuous Nurturing Automation for leads that are available for nurturing.
            </Typography>
            {ctrl.hasLaunchDate ? (
              <Alert severity="info" variant="outlined">
                This dealership has already been launched{ctrl.launchDateDisplay ? ` on ${ctrl.launchDateDisplay}` : ''}.
              </Alert>
            ) : null}
          </>
        }
        confirmLabel="Launch"
        loading={ctrl.launching}
        loadingLabel="Launching..."
      />
    </>
  );
};
